import { useQuery, useMutation, useQueryClient, QueryKey } from '@tanstack/react-query';
import { useAuth } from '../contexts/auth-context';
import { supabase } from '../lib/supabase-client';
import { Database } from '../types/supabase'; // Assuming generated types

// Define the type for the patient profile data based on the public.patients table schema
// We can use the generated Supabase types if available and accurate
export type PatientProfile = Database['public']['Tables']['patients']['Row'];

// Define the type for data needed to update the profile
export type UpdateProfileData = Partial<Omit<PatientProfile, 'id' | 'user_id' | 'created_at' | 'updated_at'>>;

// Query keys for profile data
export const profileKeys = {
  all: ['profile'] as const,
  detail: (userId: string | undefined) => [...profileKeys.all, 'detail', userId] as const,
};

/**
 * Fetches the patient profile associated with the current user.
 * @param userId The ID of the authenticated user.
 * @returns The patient profile data.
 * @throws If the user ID is missing or the profile is not found.
 */
const fetchProfile = async (userId: string | undefined): Promise<PatientProfile> => {
  if (!userId) {
    throw new Error('User is not authenticated.');
  }

  const { data, error } = await supabase
    .from('patients')
    .select('*')
    .eq('user_id', userId)
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('Error fetching profile:', error);
    throw new Error(error.message || 'Failed to fetch patient profile.');
  }

  if (!data) {
    throw new Error('Patient profile not found for this user.');
  }

  return data;
};

/**
 * Updates the patient profile.
 * @param userId The ID of the user whose profile is being updated.
 * @param updates The partial profile data to update.
 * @returns The updated patient profile data.
 * @throws If the user ID is missing or the update fails.
 */
const updateProfile = async ({ userId, updates }: { userId: string | undefined; updates: UpdateProfileData }): Promise<PatientProfile> => {
  if (!userId) {
    throw new Error('User is not authenticated.');
  }

  // Find the patient record ID based on user_id first
  const { data: existingProfile, error: fetchError } = await supabase
    .from('patients')
    .select('id')
    .eq('user_id', userId)
    .single();

  if (fetchError || !existingProfile) {
    console.error('Error finding profile to update:', fetchError);
    throw new Error(fetchError?.message || 'Could not find patient profile to update.');
  }

  const patientId = existingProfile.id;

  // Now update using the patient ID
  const { data, error } = await supabase
    .from('patients')
    .update(updates)
    .eq('id', patientId)
    .select()
    .single();

  if (error) {
    console.error('Error updating profile:', error);
    throw new Error(error.message || 'Failed to update patient profile.');
  }

  if (!data) {
    throw new Error('Failed to update profile, no data returned.');
  }

  return data;
};

/**
 * Custom hook to fetch and manage the patient profile data.
 */
export const useProfile = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id;

  const { 
    data: profile, 
    isLoading: isLoadingProfile, 
    error: profileError,
    refetch: refetchProfile,
  } = useQuery<PatientProfile, Error, PatientProfile, QueryKey>({
    queryKey: profileKeys.detail(userId),
    queryFn: () => fetchProfile(userId),
    enabled: !!userId, // Only run the query if the user ID is available
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });

  const { 
    mutate: updateProfileMutate, 
    isPending: isUpdatingProfile, 
    error: updateProfileError,
  } = useMutation<PatientProfile, Error, UpdateProfileData>({
    mutationFn: (updates) => updateProfile({ userId, updates }),
    onSuccess: (updatedData) => {
      // Invalidate and refetch the profile query after a successful update
      queryClient.invalidateQueries({ queryKey: profileKeys.detail(userId) });
      // Optionally, directly update the query cache for a faster UI update
      queryClient.setQueryData(profileKeys.detail(userId), updatedData);
    },
    onError: (error) => {
      console.error("Mutation error updating profile:", error);
      // Handle error (e.g., show toast notification)
    },
  });

  return {
    profile,
    isLoadingProfile,
    profileError,
    refetchProfile,
    updateProfile: updateProfileMutate,
    isUpdatingProfile,
    updateProfileError,
  };
}; 